/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { BoardsApiConfiguration } from '../boards-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CostDistributionViewModel } from '../models/cost-distribution-view-model';
import { CreateCostDistributionViewModel } from '../models/create-cost-distribution-view-model';
import { UpdateCostDistributionViewModel } from '../models/update-cost-distribution-view-model';

@Injectable({
  providedIn: 'root',
})
export class CostDistributionService extends BaseService {
  constructor(config: BoardsApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getCostDistributions
   */
  static readonly GetCostDistributionsPath = '/boards/{boardId}/costdistributions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCostDistributions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostDistributions$Response(params: {
    boardId: string;

    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    cccId?: string;
    stakeholderId?: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<Array<CostDistributionViewModel>>> {
    const rb = new RequestBuilder(this.rootUrl, CostDistributionService.GetCostDistributionsPath, 'get');
    if (params) {
      rb.path('boardId', params.boardId, {});
      rb.query('organisationId', params.organisationId, {});
      rb.query('cccId', params.cccId, {});
      rb.query('stakeholderId', params.stakeholderId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<CostDistributionViewModel>>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCostDistributions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostDistributions(params: {
    boardId: string;

    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    cccId?: string;
    stakeholderId?: string;
    context?: HttpContext;
  }): Observable<Array<CostDistributionViewModel>> {
    return this.getCostDistributions$Response(params).pipe(
      map((r: StrictHttpResponse<Array<CostDistributionViewModel>>) => r.body as Array<CostDistributionViewModel>)
    );
  }

  /**
   * Path part for operation createCostDistribution
   */
  static readonly CreateCostDistributionPath = '/boards/{boardId}/costdistributions';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCostDistribution()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCostDistribution$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    context?: HttpContext;
    body: CreateCostDistributionViewModel;
  }): Observable<StrictHttpResponse<CostDistributionViewModel>> {
    const rb = new RequestBuilder(this.rootUrl, CostDistributionService.CreateCostDistributionPath, 'post');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostDistributionViewModel>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createCostDistribution$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCostDistribution(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    context?: HttpContext;
    body: CreateCostDistributionViewModel;
  }): Observable<CostDistributionViewModel> {
    return this.createCostDistribution$Response(params).pipe(
      map((r: StrictHttpResponse<CostDistributionViewModel>) => r.body as CostDistributionViewModel)
    );
  }

  /**
   * Path part for operation getCostDistribution
   */
  static readonly GetCostDistributionPath = '/boards/{boardId}/costdistributions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCostDistribution()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostDistribution$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<CostDistributionViewModel>> {
    const rb = new RequestBuilder(this.rootUrl, CostDistributionService.GetCostDistributionPath, 'get');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostDistributionViewModel>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCostDistribution$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostDistribution(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
  }): Observable<CostDistributionViewModel> {
    return this.getCostDistribution$Response(params).pipe(
      map((r: StrictHttpResponse<CostDistributionViewModel>) => r.body as CostDistributionViewModel)
    );
  }

  /**
   * Path part for operation deleteCostDistribution
   */
  static readonly DeleteCostDistributionPath = '/boards/{boardId}/costdistributions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCostDistribution()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCostDistribution$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, CostDistributionService.DeleteCostDistributionPath, 'delete');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteCostDistribution$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCostDistribution(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
  }): Observable<void> {
    return this.deleteCostDistribution$Response(params).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Path part for operation updateCostDistribution
   */
  static readonly UpdateCostDistributionPath = '/boards/{boardId}/costdistributions/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCostDistribution()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCostDistribution$Response(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
    body: UpdateCostDistributionViewModel;
  }): Observable<StrictHttpResponse<CostDistributionViewModel>> {
    const rb = new RequestBuilder(this.rootUrl, CostDistributionService.UpdateCostDistributionPath, 'patch');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostDistributionViewModel>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateCostDistribution$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCostDistribution(params: {
    /**
     * Organisation the user is acting under for this operation.
     */
    organisationId: string;
    boardId: string;
    id: string;
    context?: HttpContext;
    body: UpdateCostDistributionViewModel;
  }): Observable<CostDistributionViewModel> {
    return this.updateCostDistribution$Response(params).pipe(
      map((r: StrictHttpResponse<CostDistributionViewModel>) => r.body as CostDistributionViewModel)
    );
  }
}
